/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';

import { post } from 'embercom/lib/ajax';

import type SunshineConfiguration from 'embercom/models/standalone/sunshine-configuration';
import type TicketsIntegration from 'embercom/models/standalone/zendesk-tickets-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import {
  calculateHighestIntegrationState,
  IntegrationState,
} from 'embercom/objects/standalone/constants';

export default class ZendeskConfiguration extends Model {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: AppService;
  @attr('string') declare subdomain: string;
  @attr('string') declare username: string;
  @attr('string') declare token: string;
  @attr('number', { defaultValue: IntegrationState.NotConfigured })
  declare ticketState: IntegrationState;
  @attr('boolean', { defaultValue: true })
  declare isConversationSyncingEnabled: boolean | undefined;

  @belongsTo('standalone/sunshine-configuration', { async: false })
  declare sunshineIntegration?: SunshineConfiguration;

  @belongsTo('standalone/zendesk-tickets-configuration', { async: false })
  declare ticketsIntegration: TicketsIntegration;

  @attr('boolean', { defaultValue: false })
  declare hasInstalledCopilot: boolean;

  @attr('boolean', { defaultValue: false })
  declare usesOauth: boolean;

  get zendeskPlatform() {
    return this.finStandaloneService.zendeskPlatform;
  }

  get highestIntegrationState() {
    return calculateHighestIntegrationState([
      this.ticketState,
      this.sunshineIntegration?.intercomMessengerState,
      this.sunshineIntegration?.sunshineMessengerState,
    ]);
  }

  get hasFinishedCopilotSetup() {
    return this.hasInstalledCopilot && this.appService.app.humanAdminsWithCopilotSeat.length > 0;
  }

  get canUpdateAPISettings() {
    return this.subdomain && this.username && this.token;
  }

  get isConnected() {
    return !this.isPendingSetupForTickets;
  }

  get canUseZendeskOAuth() {
    return this.appService.app.canUseZendeskOAuth;
  }

  get shouldZendeskAPIShowOAuthUI() {
    return (
      this.canUseZendeskOAuth &&
      ((this.isConnected && this.usesOauth) || this.isPendingSetupForTickets)
    );
  }

  get shouldShowZendeskApiOauthBanner() {
    return this.canUseZendeskOAuth && this.isConnected && !this.usesOauth;
  }

  get canUseSunshineOAuth() {
    return this.appService.app.canUseSunshineOAuth;
  }

  get shouldShowSunshineOAuthUI() {
    return (
      this.canUseSunshineOAuth &&
      ((this.sunshineIntegration?.isSunshineConnected && this.sunshineIntegration?.usesOauth) ||
        this.sunshineIntegration?.isSunshineDisconnected)
    );
  }

  get isSunshineConnectedWithOauth(): boolean {
    return Boolean(
      this.sunshineIntegration?.isSunshineConnected && this.sunshineIntegration?.usesOauth,
    );
  }

  get shouldShowSunshineApiOauthBanner() {
    return (
      this.canUseSunshineOAuth &&
      this.sunshineIntegration?.isSunshineConnected &&
      !this.sunshineIntegration?.usesOauth
    );
  }

  get shouldShowOauthBanner() {
    return this.shouldShowZendeskApiOauthBanner || this.shouldShowSunshineApiOauthBanner;
  }

  get canUpdateSunshineSettings() {
    return (
      this.sunshineIntegration &&
      this.sunshineIntegration.sunshineAppId &&
      this.sunshineIntegration.apiKeyId &&
      this.sunshineIntegration.apiSecret &&
      !this.isPendingSetupForTickets &&
      !this.isFinEnabledForIntercomMessenger
    );
  }

  get canUpdateSunshineOauthSettings() {
    return (
      this.sunshineIntegration &&
      !this.sunshineIntegration.usesOauth &&
      !this.isPendingSetupForTickets &&
      !this.isFinEnabledForIntercomMessenger
    );
  }

  get isEnabledForTickets() {
    return this.ticketState === IntegrationState.Enabled;
  }

  get isPausedForTickets() {
    return this.ticketState === IntegrationState.Paused;
  }

  get isPendingSetupForTickets() {
    return this.ticketState === IntegrationState.NotConfigured;
  }

  get canRevokeTicketsApi() {
    return (
      !this.isEnabledForTickets &&
      !this.isEnabledForSunshine &&
      !this.isFinEnabledForIntercomMessenger &&
      !this.isSunshineConnectedWithOauth
    );
  }

  get canRevokeSunshineApi() {
    return !this.isEnabledForSunshine;
  }

  get isFinEnabledForIntercomMessenger() {
    return this.finStandaloneService.intercomMessengerConfig.isMessengerEnabled;
  }

  get isEnabledForSunshine() {
    return (
      this.sunshineIntegration?.isEnabledForSunshineMessenger ||
      this.sunshineIntegration?.isEnabledForIntercomMessenger
    );
  }

  get fullZendeskUrl(): string | undefined {
    if (!this.subdomain) {
      return undefined;
    }
    return `https://${this.subdomain}.zendesk.com`;
  }

  get adminBaseUrl(): string | undefined {
    if (!this.subdomain) {
      return undefined;
    }

    return `https://${this.subdomain}.zendesk.com/admin`;
  }

  async revoke() {
    await post(`/ember/standalone/zendesk_configurations/${this.id}/revoke`);
    this.token = '';
    await this.reload();
    taskFor(this.zendeskPlatform.zendeskDataLoader.loadZendeskTicketsSetupData).perform();
  }

  async revokeSunshine() {
    await post(`/ember/standalone/zendesk_configurations/${this.id}/revoke_sunshine`);
    this.sunshineIntegration?.unloadRecord();
    await this.reload();
  }

  async revokeEndUserAuth() {
    await post(`/ember/standalone/zendesk_configurations/${this.id}/revoke_end_user_auth`);
    this.sunshineIntegration?.unloadRecord();
    await this.reload();
  }

  async setFinUser(finUserId: number) {
    await post(`/ember/standalone/zendesk_configurations/${this.id}/set_fin_user_for_tickets`, {
      fin_zendesk_user_id: finUserId,
    });

    this.ticketsIntegration.finZendeskUserId = finUserId;

    taskFor(this.zendeskPlatform.zendeskDataLoader.loadZendeskTicketsSetupData).perform();

    await this.reload();
  }

  async activateForTickets() {
    await post(`/ember/standalone/zendesk_configurations/${this.id}/activate_for_tickets`);
    await this.reload();
  }

  async deactivateForTickets() {
    await post(`/ember/standalone/zendesk_configurations/${this.id}/deactivate_for_tickets`);
    await this.reload();
  }

  async activateForMessenger() {
    await post(`/ember/standalone/zendesk_configurations/${this.id}/activate_for_messenger`);
    await this.reload();
  }

  async deactivateForMessenger() {
    await post(`/ember/standalone/zendesk_configurations/${this.id}/deactivate_for_messenger`);
    await this.reload();
  }
}
